import {
	BaseServiceSetup,
	Experiment,
	addToTargetingExperiment,
	defineExperiment,
	getExperiment,
	isExperimentEnabled,
	utils,
	context,
	communicationService,
	AdSlotEvent,
	CookieStorageAdapter,
	AdSlotStatus,
} from '@wikia/ad-engine';
import { Injectable } from '@wikia/dependency-injection';

type RunningExperimentType = Experiment | null | undefined;

const logGroup = 'UcpDesktopFloorAdhesionWithBiddersExperiment';

@Injectable()
export class UcpDesktopFloorAdhesionWithBiddersExperiment extends BaseServiceSetup {
	private pathfinderExperimentVariantNames = {
		control: 'aden-14863-control',
		variant1: 'aden-14863-variant1',
		variant2: 'aden-14863-variant2',
	};

	/* Buckets were assigned.
	 * They can be viewed in the below JIRA ticket comment:
	 * https://fandom.atlassian.net/browse/ADEN-14863?focusedCommentId=885901
	 * */
	private pathfinderActiveExperimentVariant: RunningExperimentType = getExperiment([
		// Single digit buckets must be padded with a 0
		defineExperiment({
			name: this.pathfinderExperimentVariantNames.variant1,
			buckets: ['07', '10', '16', '19', '22', '25', '28', '31', '34', '37'],
		}),
		defineExperiment({
			name: this.pathfinderExperimentVariantNames.variant2,
			buckets: ['08', '11', '17', '20', '23', '26', '29', '32', '35', '38'],
		}),
		defineExperiment({
			name: this.pathfinderExperimentVariantNames.control,
			buckets: ['06', '09', '15', '18', '21', '24', '27', '30', '33', '36'],
		}),
	]);

	private readonly FLOOR_ADHESION_SLOT_NAME = 'floor_adhesion';
	/**
	 * This cookie that's a similar naming convention to the interstitial unit's frequency cap cookie (_ae_intrsttl_imp)
	 * @see {@link applyCodeLevelBundles}
	 * */
	private readonly FLOOR_ADHESION_IMPRESSION_COOKIE = '_ae_d_fl_adh_exp_imp';

	// There's only 1 size set for all the bidders for the desktop floor_adhesion slot.
	private readonly STATIC_FLOOR_ADHESION_SIZES = [[728, 90]];
	private readonly BIDDER_CONFIGURATIONS = {
		/**
		 * The prebid configurations are sourced from this spreadsheet. @see {@link https://docs.google.com/spreadsheets/d/1LAJXYgNPbXhKKLk0h4EodTscjgBoBVkeuYaBM6hrirU/edit?gid=0#gid=0}
		 * The spreadsheet is also attached in this ticket @see {@link https://fandom.atlassian.net/browse/ADEN-14863}
		 *
		 * The object structure for each bidder includes the slot name as the first key, which will be added to the bidder.
		 * The nested object within the slot name key contains the unique structure that each bidder adapter accepts.
		 * @see {@link UcpDesktopPrebidConfigSetup} and each of the prebid adapter context functions that returns unique configurations per bidder.
		 * */
		prebid: {
			medianet: {
				floor_adhesion: {
					cid: '8CU5JOKX4',
					crid: '583933732',
					sizes: this.STATIC_FLOOR_ADHESION_SIZES,
				},
			},
			kargo: {
				floor_adhesion: {
					sizes: this.STATIC_FLOOR_ADHESION_SIZES,
					placementId: '_guwoHErOkN',
				},
			},
			rubicon_display: {
				floor_adhesion: {
					sizes: this.STATIC_FLOOR_ADHESION_SIZES,
					targeting: {
						loc: ['footer'],
					},
					siteId: '148804',
					zoneId: '3777180',
				},
			},
			openx: {
				floor_adhesion: {
					sizes: this.STATIC_FLOOR_ADHESION_SIZES,
					unit: '562606147',
				},
			},
			pubmatic: {
				floor_adhesion: {
					sizes: this.STATIC_FLOOR_ADHESION_SIZES,
					/**
					 * Pubmatic uses for ids and adSlot.
					 * adSlot is the newest field that should be used.
					 * The mid_boxad slot was also configured with it.
					 *
					 * @see {@link getPubmaticContext}
					 */
					adSlot: ['6908003'],
				},
			},
			triplelift: {
				floor_adhesion: {
					sizes: this.STATIC_FLOOR_ADHESION_SIZES,
					inventoryCodes: ['Fandom_desktop_adhesion_728x90_Prebid'],
				},
			},
			ix: {
				floor_adhesion: {
					sizes: this.STATIC_FLOOR_ADHESION_SIZES,
					siteId: '1206717',
				},
			},
			appnexus: {
				floor_adhesion: {
					position: 'footer',
					sizes: this.STATIC_FLOOR_ADHESION_SIZES,
					/**
					 * The appnexus prebid adapter has the option to source from either
					 * a placementId or a positions object, which contains a mapping of placementIds.
					 * @see {@link Appnexus#getBids}
					 * */
					placementId: '35630252',
				},
			},
			seedtag: {
				floor_adhesion: {
					sizes: this.STATIC_FLOOR_ADHESION_SIZES,
					publisherId: '4677-9425-01',
					adUnitId: '33908054',
					placement: 'inScreen',
				},
			},
			nobid: {
				floor_adhesion: {
					sizes: this.STATIC_FLOOR_ADHESION_SIZES,
					siteId: '21872987104',
				},
			},
			/**
			 * Refers to magniteS2s.ts prebid bidder config file.
			 * @see {@link getMagniteS2sContext}
			 * */
			mgnipbs: {
				floor_adhesion: {
					sizes: this.STATIC_FLOOR_ADHESION_SIZES,
				},
			},
		},
		a9: {
			floor_adhesion: {
				sizes: this.STATIC_FLOOR_ADHESION_SIZES,
			},
		},
	};

	private getGlobalPageViewCount(): number {
		// Get the global page view count, or force it to 0 if it's not set
		return window.fandomContext.tracking?.pvNumberGlobal ?? 0;
	}

	private isFloorAdhesionFrequencyCapMet(frequencyCap: number): boolean {
		if (frequencyCap <= 0) {
			return false;
		}
		// Even though we're not incrementing the cookie's value, and its always set to 1,
		// lets future-proof this function by parsing the cookie's value, in case the incrementation of the cookie
		// will be required for another experiment with different variants, or if this gets Productized.
		const parsedImpressionCookieValue = parseInt(this.getFloorAdhesionImpressionCookie());
		if (isNaN(parsedImpressionCookieValue)) {
			return false;
		}
		return parsedImpressionCookieValue >= frequencyCap;
	}

	private isEligibleForRender(
		renderAfterGlobalPVCount: number,
		isFrequencyCapMetCallback: () => boolean,
	) {
		// Do two checks:
		//  1. Check whether the global page view count allows for the floor_adhesion unit to be render
		//  2. Check whether a frequency cap on how many times the floor_adhesion unit can be shown was reached, or if one is even set.
		//    2a. This should all be checked within the isFrequencyCapMetCallback, and return a true/false result.
		return (
			this.getGlobalPageViewCount() > renderAfterGlobalPVCount && isFrequencyCapMetCallback?.()
		);
	}

	getFloorAdhesionImpressionCookie() {
		const cookieAdapter = new CookieStorageAdapter();
		return cookieAdapter.getItem(this.FLOOR_ADHESION_IMPRESSION_COOKIE);
	}

	updateFloorAdhesionImpressionCookie() {
		const cookieAdapter = new CookieStorageAdapter();
		/*
		 * setting TTL for this cookie to 7 days (24h * 7 days)
		 * The cookie name stands for AdEngine Desktop Adhesion Experiment Impression.
		 *
		 * Let's just set this cookie's to 1, since we don't have an impression frequency cap higher than 1.
		 * We can always adjust this function to increment the cookie's value by 1, if we need to.
		 */
		cookieAdapter.setItem(this.FLOOR_ADHESION_IMPRESSION_COOKIE, '1', 86400000 * 7);
	}

	listenForAdhesionSlotViewedAndUpdateImpressionCookie() {
		communicationService.onSlotEvent(
			AdSlotEvent.SLOT_RENDERED_EVENT,
			({ payload }) => {
				const { adType }: { event: googletag.events.SlotRenderEndedEvent; adType: string } =
					payload;

				// Check if the ad was collapsed or if GAM returned an empty ad.
				if (adType === AdSlotStatus.STATUS_NO_AD || adType === AdSlotStatus.STATUS_COLLAPSE) {
					utils.logger(
						logGroup,
						`${AdSlotEvent.SLOT_RENDERED_EVENT} - ${this.FLOOR_ADHESION_SLOT_NAME} had a ${adType} status. floor_adhesion impression cookie will not be set`,
					);
					return;
				}
				utils.logger(
					logGroup,
					`${AdSlotEvent.SLOT_RENDERED_EVENT} triggered. Applying floor_adhesion impression cookie.`,
				);
				this.updateFloorAdhesionImpressionCookie();
			},
			this.FLOOR_ADHESION_SLOT_NAME,
		);
	}

	isControl(activeExperimentName?: string): boolean {
		return activeExperimentName === this.pathfinderExperimentVariantNames.control;
	}

	isVariant1(activeExperimentName?: string): boolean {
		return activeExperimentName === this.pathfinderExperimentVariantNames.variant1;
	}

	isVariant2(activeExperimentName?: string): boolean {
		return activeExperimentName === this.pathfinderExperimentVariantNames.variant2;
	}

	addBidderConfigurationsIfApplicable(isEligibleForRender: boolean) {
		/* If the floor adhesion unit can render, then return an object with the bidderConfigurations set.
		 * Otherwise, return an empty object that won't set any bidder configurations. This means prebid and a9 configs will not be applied.
		 *
		 * It's expected that the results of this function should be applied with a spread operator,
		 * if they're being spread into the configuration object, which is the intended use for this helper function.
		 *
		 * Example: ...this.addBidderConfigurationsIfApplicable(boolean);
		 */
		return isEligibleForRender
			? {
					bidderConfigurations: this.BIDDER_CONFIGURATIONS,
				}
			: {};
	}

	setupFloorAdhesionWithBiddersExperiment() {
		if (
			isExperimentEnabled(
				this.instantConfig,
				'floorAdhesionWithBiddersDesktop',
				this.pathfinderActiveExperimentVariant,
			)
		) {
			// Definition of variants and control groups can be found in https://fandom.atlassian.net/browse/ADEN-14863
			const activeExperimentName = this.pathfinderActiveExperimentVariant?.name;
			if (this.isControl(activeExperimentName)) {
				// Do nothing, just track the control.
				utils.logger(logGroup, 'Control triggered');
				addToTargetingExperiment(this.pathfinderExperimentVariantNames.control);
			} else if (this.isVariant1(activeExperimentName)) {
				/** Experiment activation criteria:
				 Variant 1
				 	- The tracking KVP will always be sent if the user is bucketed in the experiment.
				   - The floor_adhesion unit will be enabled and the bidder configurations will be applied
								only on the 2nd pageview (global) and above. There is a frequency cap of 1. The unit stop
								being eligible for rendering, if it has been shown once.
								The frequency cap is controlled by a cookie:
				 					@see {@link getFloorAdhesionImpressionCookie}
									@see {@link updateFloorAdhesionImpressionCookie}
						- The floor_adhesion unit will be completely disabled for the bucketed user if the floor_adhesion
							render eligibility criteria are not met. This behavior is different from the control group's,
							where the floor_adhesion unit is enabled.*/
				const isEligibleForRender = this.isEligibleForRender(
					1,
					() => !this.isFloorAdhesionFrequencyCapMet(1),
				);

				utils.logger(
					logGroup,
					'Variant 1 floor_adhesion render eligibility:',
					isEligibleForRender,
					`The floor_adhesion unit and the bidders configurations will ${!isEligibleForRender ? 'NOT ' : ''}be applied.`,
				);

				if (isEligibleForRender) {
					this.listenForAdhesionSlotViewedAndUpdateImpressionCookie();
				}

				addToTargetingExperiment(this.pathfinderExperimentVariantNames.variant1);

				context.set('experiments.floorAdhesionWithBidders', {
					variant: 1,
					// Desktop Adhesion will show only after 1st (global) page view. Capped to 1 per session.
					eligibleForRender: isEligibleForRender,
					// Desktop Adhesion (floor_adhesion slot) will be added to Prebid and A9.
					...this.addBidderConfigurationsIfApplicable(isEligibleForRender),
				});

				utils.logger(logGroup, 'Variant 1 triggered');
			} else if (this.isVariant2(activeExperimentName)) {
				/** Experiment activation criteria:
				 Variant 2
				 	- The tracking KVP will always be sent if the user is bucketed in the experiment.
				   - The floor_adhesion unit will be enabled and the bidder configurations will be applied
								only on the 6th pageview (global) and above. There is no frequency cap. The unit will
							  always be configured after the 5th pageview
						- The floor_adhesion unit will be completely disabled for the bucketed user if the floor_adhesion
							render eligibility criteria are not met. This behavior is different from the control group's,
							where the floor_adhesion unit is enabled.*/

				const isEligibleForRender = this.isEligibleForRender(
					5,
					() => !this.isFloorAdhesionFrequencyCapMet(0),
				);

				utils.logger(
					logGroup,
					'Variant 2 floor_adhesion render eligibility:',
					isEligibleForRender,
					`The floor_adhesion unit and the bidders configurations will ${!isEligibleForRender ? 'NOT ' : ' '}be applied.`,
				);

				// Since the frequency cap is 0, we will not set up a listener for
				// setting the impression cookie like we did for variant1.

				addToTargetingExperiment(this.pathfinderExperimentVariantNames.variant2);

				context.set('experiments.floorAdhesionWithBidders', {
					variant: 2,
					// Desktop Adhesion will show only after 5th (global) page view. There is no cap per session.
					eligibleForRender: isEligibleForRender,
					...this.addBidderConfigurationsIfApplicable(isEligibleForRender),
				});
				utils.logger(logGroup, 'Variant 2 triggered');
			} else {
				// Do nothing, not even tracking, since we're outside the experiment buckets.
				utils.logger(logGroup, 'No variant triggered');
			}
		}
	}

	call(): void {
		this.setupFloorAdhesionWithBiddersExperiment();
	}
}
